/**
 * Platform detection utilities for PWA install flows.
 *
 * iOS Safari never fires `beforeinstallprompt`, so the standard
 * `useInstallPrompt` Android/Chromium path can't detect installability there.
 * These helpers let callers build a manual "Add to Home Screen" fallback.
 */

interface NavigatorWithStandalone extends Navigator {
  standalone?: boolean;
}

/**
 * True when running in Mobile Safari on iOS/iPadOS (excluding other
 * browsers that spoof the Safari UA token on iOS, e.g. Chrome/Firefox/Edge).
 *
 * Modern iPadOS Safari reports a desktop `Macintosh` UA (no `iPad` token),
 * so it's detected separately via the touch-capable heuristic
 * (`maxTouchPoints > 1`), which real Macs don't have.
 */
export const isIOSSafari = (): boolean => {
  if (typeof window === 'undefined') return false;
  const ua = window.navigator.userAgent;
  const isSafari = /Safari/.test(ua) && !/CriOS|FxiOS|EdgiOS/.test(ua);
  const isIOSDevice = /iPad|iPhone|iPod/.test(ua) && !(window as unknown as { MSStream?: unknown }).MSStream;
  const isIPadDesktopUA = window.navigator.maxTouchPoints > 1 && /Macintosh/.test(ua);
  return (isIOSDevice || isIPadDesktopUA) && isSafari;
};

/**
 * Display modes an installed PWA can launch in. Any match = installed.
 * Single source of truth: `useInstallPrompt` listens to these same queries.
 */
export const STANDALONE_DISPLAY_MODES: readonly string[] = [
  'standalone',
  'minimal-ui',
  'window-controls-overlay',
];

/**
 * True when the app is already running as an installed/standalone PWA
 * (any installed display-mode media query, or the iOS-specific
 * `navigator.standalone` flag).
 */
export const isStandalone = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    const matchesDisplayMode = STANDALONE_DISPLAY_MODES.some(
      (mode) => window.matchMedia(`(display-mode: ${mode})`).matches,
    );
    const isIOSStandalone = (window.navigator as NavigatorWithStandalone).standalone === true;
    return matchesDisplayMode || isIOSStandalone;
  } catch {
    return false;
  }
};
